import { useRef, useEffect } from 'react'

/* ───────────────────────────────────────────────────────────────
 *  EVENT MODAL CONFIG — tune shapes here
 * ─────────────────────────────────────────────────────────────── */
const CONFIG = {
  // ── Border radii ──
  modalBorderRadius: 'var(--radius-2xl)',
  titleInputBorderRadius: '15px',
  descInputBorderRadius: '15px',
  dateInputBorderRadius: '30px',
  timeInputBorderRadius: '30px',
  submitButtonBorderRadius: '30px',

  // ── Divider between Starts and Ends ──
  dividerColor: 'rgba(255,255,255,0.08)',
  dividerHeight: '1px',
  dividerMargin: '20px 0 12px',

  // ── Date / Time pills (Starts + Ends) ──
  datePillPadding: '5px',           // controls height — bigger = taller
  datePillFontSize: '20px',
  timePillPadding: '5px',           // controls height
  timePillFontSize: '20px',
  datePillFlex: 1,                   // how wide date is (1 = fills remaining space)
  timePillWidth: '100%',
  pillTextAlign: 'center', 
  datePillColor: 'var(--color-text-primary)',   // force white on both desktop + mobile            // 'auto' sizes to content, or set e.g. '130px'
}

function EventModal({ show, editing, eventData, setEventData, onSubmit, onClose, posting }) {
  const descRef = useRef(null)

  // Auto-resize description textarea whenever its value changes
  useEffect(() => {
    if (!show) return
    const ta = descRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = Math.min(ta.scrollHeight, 240) + 'px'
  }, [eventData?.description, show])

  if (!show) return null

  const inputBase = {
    width: '100%', padding: '12px',
    background: 'var(--color-bg-elevated)', border: 'none',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--text-lg)', outline: 'none',
    fontFamily: 'var(--font-system)',
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }} onClick={onClose}>
      <div className="animate-slide-up-slow" style={{ background: 'var(--color-bg-card-heavy)', backdropFilter: 'blur(var(--glass-blur))', WebkitBackdropFilter: 'blur(var(--glass-blur))', borderRadius: CONFIG.modalBorderRadius, padding: '24px', maxWidth: '400px', width: '100%', border: 'var(--border-input)' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-semibold)', marginBottom: '20px', color: 'var(--color-text-primary)', fontFamily: 'var(--font-system)' }}>{editing ? 'Edit Event' : 'Create Event'}</h2>

        <input
          type="text"
          placeholder="Title"
          value={eventData.title}
          onChange={e => setEventData({ ...eventData, title: e.target.value })}
          style={{ ...inputBase, borderRadius: CONFIG.titleInputBorderRadius, marginBottom: '12px' }}
        />

        <textarea
          ref={descRef}
          placeholder="Description"
          value={eventData.description}
          onChange={e => {
            setEventData({ ...eventData, description: e.target.value })
            e.target.style.height = 'auto'
            e.target.style.height = Math.min(e.target.scrollHeight, 240) + 'px'
          }}
          rows={3}
          style={{
            ...inputBase,
            borderRadius: CONFIG.descInputBorderRadius,
            marginBottom: '12px',
            resize: 'none',
            minHeight: '80px',
            maxHeight: '240px',
            lineHeight: '1.4',
            overflowY: 'auto',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        />

        {/* ── Starts ── */}
        <div style={{ marginBottom: '0' }}>
          <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block', fontFamily: 'var(--font-system)' }}>Starts</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="date"
              value={eventData.startDate}
              onChange={e => setEventData({ ...eventData, startDate: e.target.value })}
              style={{ ...inputBase, padding: CONFIG.datePillPadding, fontSize: CONFIG.datePillFontSize, borderRadius: CONFIG.dateInputBorderRadius, flex: CONFIG.datePillFlex, textAlign: CONFIG.pillTextAlign, color: CONFIG.datePillColor }}
            />
            <input
              type="time"
              value={eventData.startTime}
              onChange={e => setEventData({ ...eventData, startTime: e.target.value })}
              style={{ ...inputBase, padding: CONFIG.timePillPadding, fontSize: CONFIG.timePillFontSize, borderRadius: CONFIG.timeInputBorderRadius, color: 'var(--color-text-link)', flex: 1, width: 0, minWidth: 0, textAlign: CONFIG.pillTextAlign }}
            />
          </div>
        </div>

        {/* ── Divider ── */}
        <div style={{ height: CONFIG.dividerHeight, background: CONFIG.dividerColor, margin: CONFIG.dividerMargin }} />

        {/* ── Ends ── */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block', fontFamily: 'var(--font-system)' }}>Ends</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="date"
              value={eventData.endDate || ''}
              onChange={e => setEventData({ ...eventData, endDate: e.target.value })}
              style={{ ...inputBase, padding: CONFIG.datePillPadding, fontSize: CONFIG.datePillFontSize, borderRadius: CONFIG.dateInputBorderRadius, flex: CONFIG.datePillFlex, textAlign: CONFIG.pillTextAlign, color: CONFIG.datePillColor }}
            />
            <input
              type="time"
              value={eventData.endTime || ''}
              onChange={e => setEventData({ ...eventData, endTime: e.target.value })}
              style={{ ...inputBase, padding: CONFIG.timePillPadding, fontSize: CONFIG.timePillFontSize, borderRadius: CONFIG.timeInputBorderRadius, color: 'var(--color-text-link)', flex: 1, width: 0, minWidth: 0, textAlign: CONFIG.pillTextAlign }}
            />
          </div>
        </div>

        <button
          onClick={onSubmit}
          disabled={posting}
          style={{ width: '100%', padding: '14px', background: 'var(--color-primary)', border: 'none', borderRadius: CONFIG.submitButtonBorderRadius, color: 'var(--color-text-primary)', fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-semibold)', cursor: posting ? 'not-allowed' : 'pointer', opacity: posting ? 0.5 : 1, fontFamily: 'var(--font-system)' }}
        >
          {posting ? (editing ? 'Updating...' : 'Creating...') : (editing ? 'Update Event' : 'Create Event')}
        </button>
      </div>
    </div>
  )
}

export default EventModal